import {
  AdvancedFilterModel,
  FilterModel,
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from 'ag-grid-enterprise';
import { marketplaceService } from '@/connect/services/marketplace';
import { Side } from '@/connect/gen/consts/commonconsts_pb';
import {
  QueryMarketplaceFilter,
  QueryMarketplaceInstrument,
  QueryMarketplaceInstrumentsRequestSchema,
  QueryMarketplaceRequestSchema,
} from '@/connect/gen/modules/apiengine/services/venue/venue_pb';
import { OmsOrderType, OrderIntent } from '@/connect/gen/consts/omsconsts_pb';
import { useSecuritiesStore } from '@/store/securities-store';
import { Security } from '@/modules/common/models/security';
import { CompanyInfo } from '@/modules/common/models';
import { AgreementInfo } from '@/modules/agreements/models';
import { Data, SettlementType } from '@/connect';
import { TopOfBookWatchlist } from '@/modules/marketplace/models';
import { RateType, create } from '@/connect';

export interface DataSourceConfig {
  ordersPageLimit: number;
  tradeIntention: Side;
  rateType: RateType;
  orderIntent: OrderIntent | null;
  ownInstruments: boolean;
  counterpartyInstruments: boolean;
  ownOrders?: boolean;
  securities: Security[] | null;
  watchlist: TopOfBookWatchlist | null;
  companies: CompanyInfo[];
  agreements: AgreementInfo[];
  settlementType: SettlementType;
  companyIdFilter?: string;
}

export function hasAnyFilters(config: DataSourceConfig): boolean {
  return (
    config.ownInstruments ||
    config.counterpartyInstruments ||
    (config.securities ? config.securities.length > 0 : false) ||
    !!config.watchlist ||
    config.companies.length > 0 ||
    config.agreements.length > 0 ||
    config.settlementType != SettlementType.UNSPECIFIED ||
    !!config.companyIdFilter
  );
}

interface GetSideFilterParams extends Omit<
  DataSourceConfig,
  'ordersPageLimit' | 'watchlistDisplayId' | 'ownInstruments' | 'counterpartyInstruments'
> {
  // for convenience match BE response shape (instead of using "Side" enum)
  direction: 'borrow' | 'lend';
  filterModel: FilterModel | AdvancedFilterModel | null;
}

export function createDataSource(config: DataSourceConfig): IServerSideDatasource {
  return {
    async getRows(params) {
      if (params.request.groupKeys.length === 0) {
        await fetchGroups(params, config);
      } else {
        await fetchItems(params, config);
      }
    },
  };
}

async function fetchGroups(params: IServerSideGetRowsParams, config: DataSourceConfig) {
  try {
    const rowData = await fetchInstruments(config, params.request.filterModel);

    params.success({
      // TODO: should switch to the backend doing the pagination,
      //  for now at least provide AGGrid with the slice that it's asking for
      rowData: rowData.slice(params.request.startRow, params.request.endRow),
      rowCount: rowData.length,
    });
  } catch {
    params.fail();
  }
}

export async function fetchInstruments(
  config: DataSourceConfig,
  filterModel: FilterModel | AdvancedFilterModel | null
): Promise<Array<Data<QueryMarketplaceInstrument>>> {
  const request = create(QueryMarketplaceInstrumentsRequestSchema, {
    tradeIntention: config.tradeIntention,
    ownInstruments: config.ownInstruments,
    counterpartyInstruments: config.counterpartyInstruments,
    watchlistDisplayId: config.watchlist?.displayId,
    ...getBothSideFilters(filterModel, config),
  });
  const response = await marketplaceService.querySecurities(request, config.companyIdFilter);
  if (!response.success) throw new Error('Failed to fetch securities');

  const rowData = Object.values(response.data.instruments).sort((a, b) =>
    (a.instrument?.ticker ?? '').localeCompare(b.instrument?.ticker ?? '')
  );

  return rowData;
}

async function fetchItems(params: IServerSideGetRowsParams, config: DataSourceConfig) {
  try {
    const queryParams = {
      tradeIntention: config.tradeIntention,
      ...getBothSideFilters(params.request.filterModel, config),
    };

    const cusip = params.request.groupKeys[0];
    queryParams.lendFilter.instruments = [cusip];
    queryParams.borrowFilter.instruments = [cusip];

    const startRow = params.request.startRow ?? 0;
    const endRow = params.request.endRow ?? config.ordersPageLimit;
    const pageLimit = endRow - startRow;
    const pageNumber = Math.floor(startRow / pageLimit) + 1;

    const request = create(QueryMarketplaceRequestSchema, {
      ...queryParams,
      pageNumber,
      pageLimit,
    });
    const response = await marketplaceService.queryMarketplace(request, config.companyIdFilter);
    if (!response.success) throw new Error('Failed to fetch marketplace');

    useSecuritiesStore().addSecurities(response.data.instruments);

    const rowData = response.data.items;
    const rowCount = rowData.length < pageLimit ? startRow + rowData.length : undefined;

    // "rowCount: undefined" indicates to AgGrid there are more rows to fetch
    params.success({ rowData, rowCount });
  } catch {
    params.fail();
  }
}

export function getBothSideFilters(
  filterModel: FilterModel | AdvancedFilterModel | null,
  config: DataSourceConfig
): { borrowFilter: QueryMarketplaceFilter; lendFilter: QueryMarketplaceFilter } {
  return {
    borrowFilter: getSideFilter({ filterModel, direction: 'borrow', ...config }),
    lendFilter: getSideFilter({ filterModel, direction: 'lend', ...config }),
  };
}

function getSideFilter({
  direction,
  filterModel,
  securities,
  companies,
  agreements,
  settlementType,
  ownOrders,
  tradeIntention,
  rateType,
}: GetSideFilterParams) {
  const isSameSide =
    (direction === 'borrow' && tradeIntention === Side.BORROW) ||
    (direction === 'lend' && tradeIntention === Side.LOAN);
  return {
    ownOrders: isSameSide || ownOrders,
    counterpartyIds: getCounterpartiesFilter(companies),
    agreementIds: getAgreementsFilter(agreements),
    instruments: securities ? securities.map((s) => s.cusip) : undefined,
    settlementType: settlementType === SettlementType.UNSPECIFIED ? undefined : settlementType,
    ...getOrderTypeFilter(direction, filterModel),
    ...getQuantityFilter(direction, filterModel),
    ...getRateFilter(direction, filterModel, rateType),
    ...getOrderIntentFilter(direction, filterModel),
  } as QueryMarketplaceFilter;
}

function getAgreementsFilter(agreements: AgreementInfo[]) {
  if (agreements.length === 0) return undefined;
  return agreements.map((c) => c.id);
}

function getCounterpartiesFilter(counterparties: CompanyInfo[]) {
  if (counterparties.length === 0) return undefined;
  return counterparties.map((c) => c.companyId);
}

function getOrderTypeFilter(
  direction: 'borrow' | 'lend',
  filterModel: FilterModel | AdvancedFilterModel | null
) {
  const filter = filterModel?.[`${direction}OrderType`];
  if (!filter) return undefined;
  const orderType = (
    filter.filter === OmsOrderType.UNSPECIFIED_ORDER_TYPE ? undefined : filter.filter
  ) as OmsOrderType;
  switch (filter.type) {
    case 'equals':
      return { orderType };
  }
  return undefined;
}

function getOrderIntentFilter(
  direction: 'borrow' | 'lend',
  filterModel: FilterModel | AdvancedFilterModel | null
) {
  const filter = filterModel?.[`${direction}OrderIntent`];
  if (!filter) return undefined;
  const orderIntent = (
    filter.filter === OrderIntent.UNSPECIFIED_ORDER_INTENT ? undefined : filter.filter
  ) as OrderIntent;
  switch (filter.type) {
    case 'equals':
      return { orderIntent };
  }
  return undefined;
}

function getQuantityFilter(
  direction: 'borrow' | 'lend',
  filterModel: FilterModel | AdvancedFilterModel | null
) {
  const filter = filterModel?.[`${direction}Quantity`];
  if (!filter) return undefined;
  switch (filter.type) {
    case 'equals':
      return {
        minimumQuantity: BigInt(filter.filter),
        maximumQuantity: BigInt(filter.filter),
      };
    case 'lessThanOrEqual':
      return { maximumQuantity: BigInt(filter.filter) };
    case 'greaterThanOrEqual':
      return { minimumQuantity: BigInt(filter.filter) };
  }
  return undefined;
}

function getRateFilter(
  direction: 'borrow' | 'lend',
  filterModel: FilterModel | AdvancedFilterModel | null,
  rateType: RateType
) {
  const filter = filterModel?.[`${direction}Rate`];
  if (!filter) return undefined;
  const rateName = rateType == RateType.FEE ? 'Fee' : 'Rebate';
  switch (filter.type) {
    case 'equals':
      return {
        [`maximum${rateName}`]: filter.filter,
        [`minimum${rateName}`]: filter.filter,
      };
    case 'lessThanOrEqual':
      return { [`maximum${rateName}`]: filter.filter };
    case 'greaterThanOrEqual':
      return { [`minimum${rateName}`]: filter.filter };
  }
  return undefined;
}
